'use client'

import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import { Avatar, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { type MouseEvent, useState } from 'react'
import useAuth from '../hooks/useAuth'
import BrandSwitcher from './brand/BrandSwitcher'

function userInitial(
  user: { fullName?: string | null; email?: string | null } | null | undefined,
): string {
  const name = user?.fullName?.trim() || user?.email?.trim() || ''
  return name ? name.charAt(0).toUpperCase() : 'U'
}

/**
 * Top-right account menu: brand switches, user settings, and log out.
 */
export default function UserAccountMenu() {
  const { user, logout, effectivePartnerType } = useAuth()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const hideUserSettings =
    effectivePartnerType === 'Demo' || effectivePartnerType === 'AI Demo'

  if (!user) {
    return null
  }

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleLogout = async () => {
    handleClose()
    await logout()
  }

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Open account menu"
        aria-controls={open ? 'user-account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        data-testid="account-menu-button"
        size="small"
      >
        <Avatar
          // biome-ignore lint/plugin: compact avatar sized to the toolbar icon button
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'text.primary',
            fontSize: 14,
          }}
        >
          {userInitial(user)}
        </Avatar>
      </IconButton>
      <Menu
        id="user-account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
            },
          },
        }}
        disableScrollLock
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <BrandSwitcher onAction={handleClose} />
        {!hideUserSettings && (
          <MenuItem component="a" href="/settings" onClick={handleClose}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            User Settings
          </MenuItem>
        )}
        <MenuItem onClick={() => void handleLogout()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </>
  )
}
